import clsx from 'clsx';
import { StyledTableRow } from '@/components/pages/kiosk/monitor/StaffMonitor';
import { alpha, ButtonBase, InputBase, TableCell } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import React, { ReactNode } from 'react';
import { TrackingSubject } from '@/components/pages/kiosk/monitor/StaffMonitor/StaffMonitor';
import { formatDuration } from '@/util/duration';
import { TableSettings } from '@/components/pages/kiosk/monitor/StaffMonitor/components/TableSettings';
import { gql, useApolloClient } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import Snackbar from '@/components/core/Snackbar';
import { withRouter } from 'react-router';

export const trackingRoleLabel = (subject: {
  role: string;
  title?: string;
  staffMember?: unknown;
  anesthesiologist?: { isCrna?: boolean | null };
}): string => {
  if (subject.anesthesiologist?.isCrna) return 'CRNA';
  if (subject.staffMember) return subject.title ?? subject.role;
  return subject.role;
};

interface MemberTableRowProps extends TrackingSubject {
  timeOfDay: number;
  compact?: boolean;
  settings: TableSettings;
  date: string;
  history: any;
  match: any;
  isLiveView: boolean;
  disableRowClick?: boolean;
}

const MemberTableRow = ({
  id,
  role,
  title: subjectTitle,
  entityId,
  physician,
  staffMember,
  anesthesiologist,
  currentLocation,
  arrivedOnSite,
  arrivedOnSiteOverride,
  leftSite,
  leftSiteOverride,
  timeOfDay,
  compact,
  settings,
  date,
  history,
  match,
  isLiveView,
  disableRowClick = false,
}: MemberTableRowProps) => {
  const classes = useStyles();
  const [isArrivalInEdit, setIsArrivalInEdit] = React.useState(false);
  const [isDepartureInEdit, setIsDepartureInEdit] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);

  // Determine actual left site status: if there's a current location (room detected),
  // ignore departure overrides since the bracelet is clearly still on-site
  const actualLeftSite = currentLocation ? null : leftSiteOverride || leftSite;

  const settingsColumns = settings.columns.filter(({ visible, key }) => {
    // Hide current location and time at location columns for historical views
    if (!isLiveView && (key === 'currentLocation' || key === 'timeAtLocation')) {
      return false;
    }
    return visible;
  });

  const apolloClient = useApolloClient();

  const handleStaffClick = () => {
    const organizationId = match.params.organizationId;
    history.push(`/kiosk/${organizationId}/staff-log/${entityId}/${role.toLowerCase()}/${date}`);
  };

  const overrideArrivedOnSite = async (time: number | null) => {
    try {
      if (physician) {
        apolloClient.mutate({
          mutation: gql`
            mutation OverridePhysicianArrivedOnSite($physicianId: Long!, $date: Date!, $time: Int) {
              overridePhysicianArrivedOnSite(physician: $physicianId, date: $date, time: $time)
            }
          `,
          variables: {
            physicianId: physician.id,
            date,
            time,
          },
          refetchQueries: ['staffMonitoring'],
        });
        setMessage('Time updated successfully.');
      }

      if (staffMember) {
        const staffMemberId = parseInt(staffMember.id.split('.')[0], 10);

        apolloClient.mutate({
          mutation: gql`
            mutation OverrideStaffMemberArrivedOnSite($staffMemberId: Long!, $date: Date!, $time: Int) {
              overrideStaffMemberArrivedOnSite(staffId: $staffMemberId, date: $date, time: $time)
            }
          `,
          variables: {
            staffMemberId,
            date,
            time,
          },
          refetchQueries: ['staffMonitoring'],
        });
        setMessage('Time updated successfully.');
      }

      if (anesthesiologist) {
        apolloClient.mutate({
          mutation: gql`
            mutation OverrideAnesthesiologistArrivedOnSite($anesthesiologistId: Long!, $date: Date!, $time: Int) {
              overrideAnesthesiologistArrivedOnSite(anesthesiologist: $anesthesiologistId, date: $date, time: $time)
            }
          `,
          variables: {
            anesthesiologistId: anesthesiologist.id,
            date,
            time,
          },
          refetchQueries: ['staffMonitoring'],
        });
        setMessage('Time updated successfully.');
      }
    } catch (error: unknown) {
      setMessage(error instanceof Error ? error.message : 'An error occurred while updating the time.');
    }
  };

  const overrideLeftSite = async (time: number | null) => {
    try {
      if (physician) {
        apolloClient.mutate({
          mutation: gql`
            mutation OverridePhysicianLeftSite($physicianId: Long!, $date: Date!, $time: Int) {
              overridePhysicianLeftSite(physician: $physicianId, date: $date, time: $time)
            }
          `,
          variables: {
            physicianId: physician.id,
            date,
            time,
          },
          refetchQueries: ['staffMonitoring'],
        });
        setMessage('Time updated successfully.');
      }

      if (staffMember) {
        const staffMemberId = parseInt(staffMember.id.split('.')[0], 10);

        apolloClient.mutate({
          mutation: gql`
            mutation OverrideStaffMemberLeftSite($staffMemberId: Long!, $date: Date!, $time: Int) {
              overrideStaffMemberLeftSite(staffId: $staffMemberId, date: $date, time: $time)
            }
          `,
          variables: {
            staffMemberId,
            date,
            time,
          },
          refetchQueries: ['staffMonitoring'],
        });
        setMessage('Time updated successfully.');
      }

      if (anesthesiologist) {
        apolloClient.mutate({
          mutation: gql`
            mutation OverrideAnesthesiologistLeftSite($anesthesiologistId: Long!, $date: Date!, $time: Int) {
              overrideAnesthesiologistLeftSite(anesthesiologist: $anesthesiologistId, date: $date, time: $time)
            }
          `,
          variables: {
            anesthesiologistId: anesthesiologist.id,
            date,
            time,
          },
          refetchQueries: ['staffMonitoring'],
        });
        setMessage('Time updated successfully.');
      }
    } catch (error: unknown) {
      setMessage(error instanceof Error ? error.message : 'An error occurred while updating the time.');
    }
  };

  const convertToMilliseconds = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return (hours * 60 + minutes) * 60 * 1000;
  };

  const applyArrivedOnSiteOverride = (value: any) => {
    const timeValue = convertToMilliseconds(value);
    setIsArrivalInEdit(false);
    overrideArrivedOnSite(isNaN(timeValue) ? null : timeValue);
  };

  const handleArrivedOnSite = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.key === 'Enter') {
      applyArrivedOnSiteOverride((e.target as HTMLInputElement).value);
    }
  };

  const handleArrivedOnSiteBlur = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    applyArrivedOnSiteOverride((e.target as HTMLInputElement).value);
  };

  const applyLeftSiteOverride = (value: any) => {
    const timeValue = convertToMilliseconds(value);
    setIsDepartureInEdit(false);
    overrideLeftSite(isNaN(timeValue) ? null : timeValue);
  };

  const handleLeftSite = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.key === 'Enter') {
      applyLeftSiteOverride((e.target as HTMLInputElement).value);
    }
  };

  const handleLeftSiteBlur = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    applyLeftSiteOverride((e.target as HTMLInputElement).value);
  };

  const allowEditing = timeOfDay === 86400000;

  return (
    <>
      <StyledTableRow>
        {settingsColumns.map(({ key, title }, index) => {
          const isLastColumn = index + 1 === settingsColumns.length;
          const departureTime = leftSiteOverride ? formatTimeOfDay(leftSiteOverride) : formatTimeOfDay(leftSite);

          switch (key) {
            case 'name':
              const displayName = physician?.name ?? staffMember?.name ?? anesthesiologist?.name ?? '-';
              return (
                <TableCell key={key} colSpan={isLastColumn ? 2 : 1} size={compact ? 'small' : 'medium'}>
                  {disableRowClick ? (
                    <Typography variant="h3" color="inherit">
                      {displayName}
                    </Typography>
                  ) : (
                    <ButtonBase onClick={handleStaffClick} className={classes.nameButton}>
                      <Typography variant="h3" color="inherit">
                        {displayName}
                      </Typography>
                    </ButtonBase>
                  )}
                </TableCell>
              );
            case 'role':
              return (
                <TableCell colSpan={isLastColumn ? 2 : 1}>
                  <Typography variant="h3" color="inherit">
                    {trackingRoleLabel({ role, title: subjectTitle, staffMember, anesthesiologist })}
                  </Typography>
                </TableCell>
              );
            case 'cost':
              const costSubject = staffMember ?? anesthesiologist;
              return (
                <TableCell colSpan={isLastColumn ? 2 : 1}>
                  <Typography variant="h3" color="inherit">
                    {formatCost(
                      calculateCost(
                        costSubject,
                        timeOfDay,
                        arrivedOnSite,
                        arrivedOnSiteOverride,
                        leftSite,
                        leftSiteOverride
                      )
                    )}
                  </Typography>
                </TableCell>
              );
            case 'currentLocation':
              return (
                <TableCell colSpan={isLastColumn ? 2 : 1} size={compact ? 'small' : 'medium'}>
                  <Typography
                    variant="h3"
                    color="inherit"
                    className={clsx(classes.locationIn, {
                      [classes.inOr]: currentLocation?.room?.type === 'OR' && !compact,
                    })}
                  >
                    {currentLocation?.room?.name ?? (actualLeftSite ? 'Off-site' : arrivedOnSite ? 'On-site' : '-')}
                  </Typography>
                </TableCell>
              );
            case 'timeAtLocation':
              return (
                <TableCell
                  colSpan={isLastColumn ? 2 : 1}
                  size={compact ? 'small' : 'medium'}
                  className={classes.nowrap}
                >
                  <Box className={classes.elapsedTime} px={1} py={0.5} display="flex" alignItems="center">
                    <Typography variant="h3" color="inherit" component="span" className={classes.elapsedTime}>
                      {currentLocation
                        ? formatDuration(currentLocation.arrivedAt, timeOfDay, true)
                        : arrivedOnSite && !actualLeftSite
                          ? formatDuration(arrivedOnSite, timeOfDay, true)
                          : '-'}
                    </Typography>
                  </Box>
                </TableCell>
              );
            case 'arrival':
              return (
                <TableCell colSpan={isLastColumn ? 2 : 1} className={classes.nowrap}>
                  {isArrivalInEdit ? (
                    <InputBase
                      type="time"
                      autoFocus
                      inputRef={input => {
                        if (input) {
                          input.select();
                        }
                      }}
                      onKeyDown={handleArrivedOnSite}
                      onBlur={handleArrivedOnSiteBlur}
                      defaultValue={formatTimeOfDay(arrivedOnSiteOverride ?? arrivedOnSite)}
                      className={classes.editValue}
                    />
                  ) : allowEditing ? (
                    <ButtonBase className={classes.editableCell} onClick={() => setIsArrivalInEdit(true)}>
                      <Typography variant="h3" color="inherit">
                        {formatTimeOfDay(arrivedOnSiteOverride ?? arrivedOnSite)}
                      </Typography>
                    </ButtonBase>
                  ) : (
                    <Typography variant="h3" color="inherit">
                      {formatTimeOfDay(arrivedOnSiteOverride ?? arrivedOnSite)}
                    </Typography>
                  )}
                </TableCell>
              );
            case 'departure':
              return (
                <TableCell colSpan={isLastColumn ? 2 : 1} className={classes.nowrap}>
                  {isDepartureInEdit ? (
                    <InputBase
                      type="time"
                      autoFocus
                      inputRef={input => {
                        if (input) {
                          input.select();
                        }
                      }}
                      onKeyDown={handleLeftSite}
                      onBlur={handleLeftSiteBlur}
                      defaultValue={formatTimeOfDay(leftSiteOverride)}
                      className={classes.editValue}
                    />
                  ) : allowEditing ? (
                    <ButtonBase className={classes.editableCell} onClick={() => setIsDepartureInEdit(true)}>
                      <Typography variant="h3" color="inherit">
                        {departureTime}
                      </Typography>
                    </ButtonBase>
                  ) : (
                    <Typography variant="h3" color="inherit">
                      {departureTime}
                    </Typography>
                  )}
                </TableCell>
              );
            case 'totalTimeOnSite':
              return (
                <TableCell colSpan={isLastColumn ? 2 : 1} className={classes.nowrap}>
                  <Typography variant="h3" color="inherit">
                    {formatDuration(
                      floorToMinute(arrivedOnSiteOverride || arrivedOnSite),
                      floorToMinute(leftSiteOverride || leftSite || timeOfDay),
                      true
                    )}
                  </Typography>
                </TableCell>
              );
            default:
              return null;
          }
        })}
      </StyledTableRow>
      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={() => setMessage(null)}>
        {message}
      </Snackbar>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  elapsedTime: {
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
  },
  locationIn: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
  },
  inOr: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: theme.shape.borderRadius,
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  editableCell: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'inherit',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 1.5),
    '&:focus': {
      border: `2px solid ${theme.palette.primary.main}`,
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      margin: -2,
    },
  },
  editValue: {
    ...theme.typography.h3,
    backgroundColor: '#020B26',
    color: '#FFFFFF',
    padding: theme.spacing(0, 1.5),
    borderRadius: theme.shape.borderRadius,
    width: 144,
    '&:focus': {},
    '&.Mui-focused': {
      border: `2px solid ${theme.palette.primary.main}`,
      backgroundColor: '#020B26',
      margin: -2,
    },
    '& > input::-webkit-calendar-picker-indicator': {
      filter: 'invert(100%)',
    },
  },
  nameButton: {
    width: '100%',
    textAlign: 'left',
    justifyContent: 'flex-start',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    color: 'inherit',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
}));

const WrappedMemberTableRow = withRouter(MemberTableRow as any);
export default WrappedMemberTableRow;

export function floorToMinute(milliseconds: number): number {
  const seconds = milliseconds / 1000;
  const minutes = seconds / 60;

  return Math.floor(minutes) * 60 * 1000;
}

export function formatTimeOfDay(milliseconds: number | null): React.ReactNode {
  if (milliseconds === null || !isFinite(milliseconds)) {
    return 'On-site';
  }

  if (milliseconds === 123000) {
    return 'Midnight';
  }

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours % 24 === 0 && minutes % 60 === 0 && seconds % 60 === 0) {
    return 'Midnight';
  }

  return (
    <>
      {String(hours % 12 || 12).padStart(2, '0')}:{String(minutes % 60).padStart(2, '0')}{' '}
      {hours % 24 >= 12 ? 'PM' : 'AM'}
    </>
  );
}

export const calculateCost = (
  costSubject: { hourlyRate?: number | null } | null | undefined,
  timeOfDay: number,
  arrivedOnSite: number,
  arrivedOnSiteOverride: number | null,
  leftSite: number | null,
  leftSiteOverride: number | null
): number | undefined => {
  if (!costSubject?.hourlyRate) {
    return;
  }

  const beginAt = arrivedOnSiteOverride || arrivedOnSite;

  if (!isFinite(beginAt)) {
    return;
  }

  const endAt =
    !leftSiteOverride && leftSite === 123000 ? 24 * 60 * 60 * 1000 : leftSiteOverride || leftSite || timeOfDay;

  if (!isFinite(endAt)) {
    return;
  }

  const duration = endAt - beginAt;

  if (duration < 0) {
    return;
  }

  const durationInStartedMinutes = Math.ceil(duration / 60 / 1000);

  return (costSubject.hourlyRate * durationInStartedMinutes) / 60;
};

export const formatCost = (cost: number | undefined): ReactNode => (cost ? `$${Math.round(cost)}` : '-');
